// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * serviceDuration — ONE brain for how long a service takes (and the shape of per-barber overrides).
 *
 * THE BUG THIS FIXES (Jose 2026-10-17): Fresha exports generic service names ("Haircut", "Beard Trim")
 * that don't match the SLIK menu's variation names ("Short Classic", "Low Fade"), so the import had
 * nothing to resolve against and wrote Fresha's raw duration verbatim (e.g. 15 min for a 30-min haircut).
 * Calendar card height = duration, so those cards rendered half-height. The fix: duration ALWAYS resolves
 * from config here — never trusted from external data.
 *
 * Resolution order (first match wins):
 *   1. per-appointment override — the caller already has it (an edited/resized card); not handled here.
 *   2. per-barber override      — opts.barberDurations[name] (the "Services & Times" tab, Jose's next ask:
 *                                 "Diana needs 45 for a fade, mine's 30"). barberId is accepted now so
 *                                 callers wire it before the override data even exists — zero rework later.
 *   3. canonical menu duration  — opts.serviceMap[name] (built from the service_categories collection).
 *   4. legacy / Fresha alias    — LEGACY_DURATIONS, for the generic names Fresha uses that aren't in the menu.
 *   5. fallback                 — opts.fallback ?? 30.
 */

/** Normalize a service name for keying: trim, lowercase, collapse whitespace. */
export const normServiceName = (s?: unknown): string =>
  String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Shop-confirmed durations for the generic/legacy service names (Jose 2026-10-17).
 * These cover what Fresha exports; the real menu variations live in service_categories.
 */
export const LEGACY_DURATIONS: Record<string, number> = {
  'haircut': 30, 'classic haircut': 30, 'short classic': 30, 'regular haircut': 30,
  'fade': 30, 'low fade': 30, 'medium fade': 30, 'high fade': 30, 'skin fade': 30,
  'beard trim': 15,
  'beard shaping': 30,
  'eyebrow waxing': 15,
  'father + son': 30, 'father and son': 30, 'father+son': 30,
  'squez': 30, 'squeeze': 30,
  'long hair': 45,
};

export interface ResolveDurationOpts {
  /** Canonical menu durations: normalized service name → minutes (from service_categories). */
  serviceMap?: Record<string, number>;
  /** Per-barber overrides for the resolved barber: normalized service name → minutes. */
  barberDurations?: Record<string, number>;
  /** Fallback when nothing matches (e.g. the raw Fresha value, else 30). */
  fallback?: number;
}

/**
 * Resolve how long `serviceName` takes, optionally for a specific barber.
 * `barberId` is part of the signature now (forward-compat for per-barber times); the override VALUES are
 * passed via opts.barberDurations so this stays a pure function with no data access.
 */
export function resolveServiceDuration(
  serviceName: string | null | undefined,
  barberId?: string | null,
  opts: ResolveDurationOpts = {},
): number {
  const key = normServiceName(serviceName);
  const { serviceMap, barberDurations, fallback = 30 } = opts;
  if (barberDurations && barberDurations[key] != null) return barberDurations[key]; // 2. per-barber
  if (serviceMap && serviceMap[key] != null) return serviceMap[key];                 // 3. canonical menu
  if (LEGACY_DURATIONS[key] != null) return LEGACY_DURATIONS[key];                    // 4. legacy alias
  return fallback;                                                                     // 5. fallback
}

export interface ResolvePriceOpts {
  /** Canonical menu prices: normalized service name → dollars (from service_categories). */
  serviceMap?: Record<string, number>;
  /** Per-barber price overrides for the resolved barber: normalized service name → dollars. */
  barberPrices?: Record<string, number>;
  /** Fallback when nothing matches (e.g. the appointment's own price). */
  fallback?: number;
}

/**
 * Resolve the PRICE of `serviceName`, optionally for a barber whose rate differs (Jose 2026-10-17:
 * "my prices may be higher than others"). Same order as duration: per-barber → canonical menu → fallback.
 * No legacy alias — prices aren't shared shop-wide the way the standard durations are.
 */
export function resolveServicePrice(
  serviceName: string | null | undefined,
  barberId?: string | null,
  opts: ResolvePriceOpts = {},
): number {
  const key = normServiceName(serviceName);
  const { serviceMap, barberPrices, fallback = 0 } = opts;
  if (barberPrices && barberPrices[key] != null) return barberPrices[key]; // per-barber rate
  if (serviceMap && serviceMap[key] != null) return serviceMap[key];        // canonical menu price
  return fallback;
}

/** Per-barber service config stored on the barber doc. Blank entries = use the shop default. */
export interface BarberServiceRates {
  serviceDurations?: Record<string, number>; // normalized service name → minutes
  servicePrices?: Record<string, number>;     // normalized service name → dollars
}

// ── FAMILY / KIDS (Jose 2026-10-25) ────────────────────────────────────────────────────────────────
// SLIK isn't a kids' shop, but we cut our clients' kids. A child's cut is QUICK — a kid doesn't take a
// full half-hour UNLESS it's a fade. So a child's non-fade service bills/blocks only 15 min; a fade (or
// any adult) takes the full time. Math: dad 30 + each kid 15 → dad+son 45, dad+2sons 60, dad+3sons 75.
// "Child" = a family member whose relationship is son/daughter (captured in Add-Family-Member).
export const CHILD_QUICK_DURATION = 15;

/** Is this service a fade? (fades need the full time even for a kid). Name OR category may carry it. */
export function isFadeService(serviceName?: string | null, categoryName?: string | null): boolean {
  return /fade/i.test(`${serviceName || ''} ${categoryName || ''}`);
}

/** Is this family-member relationship a CHILD (gets the quick-cut duration)? Adults (parent/spouse) = full. */
export function isChildRelationship(relationship?: string | null): boolean {
  const r = String(relationship || '').toLowerCase().trim();
  return r === 'son' || r === 'daughter' || r === 'child' || r === 'kid';
}

/**
 * The shop's BASE regular cut — the ONLY service that gets the reduced family/kid time by default.
 * EVERYTHING else (medium, long, FADE, anything) blocks the FULL time automatically. (Jose 2026-11-03:
 * "father+son is just short classics; the second they choose anything else it's full-time.") Matches
 * "Short Classic" / "Classic Haircut" by the word "classic"; fades/medium/long don't carry it.
 */
export function isBaseClassic(serviceName?: string | null, categoryName?: string | null): boolean {
  return /\bclassic\b/i.test(`${serviceName || ''} ${categoryName || ''}`);
}

/**
 * Per-CLIENT cut-time config the barber sets on a client's profile / appointment card (staff). ONE source
 * of truth on `clients/{id}.cutTimeSettings`, read identically by the app Family button and the phone
 * receptionist (Ace). (Jose 2026-10-26 + 2026-11-03.)
 */
export interface CutTimeSettings {
  soloMinutes?: number;    // explicit duration ALONE — the base-classic knob (overrides the service default)
  familyMinutes?: number;  // explicit duration WITH family — the base-classic knob (overrides kid-15 / adult-full)
  // PER-SERVICE, PER-CLIENT minutes (Jose 2026-11-03): the exact time for a SPECIFIC service for THIS client
  // ("his medium is 40", "his fade is 25"). Keyed by normalized service name. Wins over everything for that
  // service — including the "non-classic = full" rule. Absent/blank ⇒ the auto rules below.
  serviceTimes?: Record<string, { family?: number; solo?: number }>;
}

/**
 * Effective duration for ONE person. Priority (first match wins):
 *   1. PER-SERVICE per-client override (cutTimeSettings.serviceTimes[service].family|solo),
 *   2. the legacy base-classic knob (familyMinutes when together / soloMinutes when alone),
 *   3. AUTO RULE — only the BASE Short Classic shrinks (child 15 / adult full); EVERY other service = full.
 * Computed in exactly ONE place so booking, the grouped card, checkout, and Ace all agree.
 *
 * `isFamilyBooking` defaults to true (back-compat with the family-only callers). Pass `false` for a solo
 * booking so the solo knob — not the family shrink — applies.
 */
export function familyMemberDuration(
  fullDuration: number,
  opts: {
    isChild: boolean;
    serviceName?: string | null;
    categoryName?: string | null;
    isFamilyBooking?: boolean;
    cutTimeSettings?: CutTimeSettings | null;
  },
): number {
  const full = fullDuration > 0 ? fullDuration : 30;
  const cts = opts.cutTimeSettings || {};
  const asFamily = opts.isFamilyBooking !== false; // family unless the caller explicitly says solo
  const svcKey = normServiceName(opts.serviceName);

  // 1. PER-SERVICE, PER-CLIENT override — the exact time the barber set for THIS service on THIS client.
  //    Wins over everything, including the non-classic=full rule ("his medium is 40", "his fade is 25").
  const perSvc = cts.serviceTimes && svcKey ? cts.serviceTimes[svcKey] : undefined;
  if (perSvc) {
    const v = asFamily ? perSvc.family : perSvc.solo;
    if (typeof v === 'number' && v > 0) return v;
  }

  // SOLO: the per-client solo knob wins; otherwise the full service duration (no shrink when alone).
  if (!asFamily) {
    return (typeof cts.soloMinutes === 'number' && cts.soloMinutes > 0) ? cts.soloMinutes : full;
  }

  // FAMILY: only the BASE Short Classic gets a reduced/configured time — EVERY other service (medium, long,
  // fade, …) blocks the FULL time, no exceptions (the config above already had its chance per-service).
  if (!isBaseClassic(opts.serviceName, opts.categoryName)) return full;

  // Base classic: the per-client family knob wins (shorter OR longer); else kid = quick-cut, adult = full.
  if (typeof cts.familyMinutes === 'number' && cts.familyMinutes > 0) return cts.familyMinutes;
  if (opts.isChild) return CHILD_QUICK_DURATION;
  return full;
}
